import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { Download } from "lucide-react";

import PageContainer from "../../components/common/PageContainer";
import useIsMobile from "../../hooks/useIsMobile";

import LeadsFilters from "../../components/leads/LeadsFilters";
import LeadsTable from "../../components/leads/LeadsTable";
import { useColumns } from "../../components/leads/leadsColumns";

import { toast } from "react-toastify";

import {
  useLeadsQuery,
  useExportLeads,
  useResendLead,
} from "../../queries/leads/useLeadsQuery";


import { usePropertiesOptionsQuery } from "../../queries/properties/usePropertiesQuery"
import { useAgentsOptionsQuery, useAgentPropertiesOptionsQuery } from "../../queries/agents/useAgentsQuery"

import { useAuthStore } from "../../store/authStore";

import Button from "../../components/ui/Button";
import Pagination from "../../components/common/pagination/Pagination";
import { normalizeLeads } from "../../utils/normalizeLeads";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100, 200];
const LEAD_CATEGORIES = ["Hot", "Warm", "Cold"];

function getCategoryFromSearch(searchParams) {
  const category = searchParams.get("category");
  if (!category) return "All";

  const normalized =
    category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();

  return LEAD_CATEGORIES.includes(normalized) ? normalized : "All";
}

export default function Leads() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();

  const { role, user } = useAuthStore();
  const isAgent = role === "agent";

  /* ---------------- PAGINATION STATE ---------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  /* ---------------- FILTER STATES (now sent to backend) ---------------- */
  const [filterCategory, setFilterCategory] = useState(() =>
    getCategoryFromSearch(searchParams)
  );
  const [filterProperties, setFilterProperties] = useState("All");
  const [filterAgent, setFilterAgent] = useState("All");
  const [filterSource, setFilterSource] = useState("All");
  const [filterIntent, setFilterIntent] = useState("All");
  const [sortDate, setSortDate] = useState("Latest");

  const { data: leadsList, isLoading: isLeadsLoading, isFetching: isLeadsFetching } =
    useLeadsQuery({
      page: currentPage,
      page_size: pageSize,
      category: filterCategory,
      property_id: filterProperties,
      agent_id: filterAgent,
      source: filterSource,
      intent: filterIntent,
      sort: sortDate,
    });


  // Always call every hook — just enable/disable via each query's `enabled` flag
  const { data: agentPropertyOptions } = useAgentPropertiesOptionsQuery(user?.id, {
    enabled: isAgent && !!user?.id,
  });

  const { data: allPropertiesOptions } = usePropertiesOptionsQuery({
    enabled: !isAgent,
  });

  const { data: allAgentsOptions } = useAgentsOptionsQuery({
    enabled: !isAgent,
  });

  const propertyOptions = useMemo(() => {
    const source = isAgent ? agentPropertyOptions : allPropertiesOptions;
    return (source ?? []).map((p) => ({ label: p.name, value: p.id }));
  }, [isAgent, agentPropertyOptions, allPropertiesOptions]);

  const agentOptions = useMemo(
    () => (isAgent ? [] : (allAgentsOptions ?? []).map((a) => ({ label: a.name, value: a.id }))),
    [isAgent, allAgentsOptions]
  );

  const { mutate: exportLeads } = useExportLeads();
  const { mutate: resendLead } = useResendLead();

  useEffect(() => {
    const nextCategory = getCategoryFromSearch(searchParams);
    setFilterCategory((prev) => (prev === nextCategory ? prev : nextCategory));
  }, [searchParams]);

  const handleCategoryChange = (value) => {
    setFilterCategory(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== "All") {
        next.set("category", value);
      } else {
        next.delete("category");
      }
      return next;
    }, { replace: true });
  };

  // component.jsx
  const leads = useMemo(() => normalizeLeads(leadsList?.data ?? []), [leadsList]);

  const pagination = leadsList?.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const totalLeads = pagination?.total ?? leads.length;


  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  /* Reset to page 1 whenever any filter changes */
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, filterProperties, filterAgent, filterSource, filterIntent, sortDate]);

  /* ---------------- ACTION ---------------- */

  const handleResend = (lead) => {
    resendLead(lead.id, {
      onSuccess: () => toast.success("Lead resent successfully"),
      onError: (error) => {
        console.error("Failed to resend lead", error);
        toast.error("Failed to resend lead");
      },
    });
  };

  const columns = useColumns();

  const handleExport = () => {
    exportLeads(undefined, {
      onSuccess: async (response) => {
        const blob = response.data;

        if (!(blob instanceof Blob)) {
          toast.error("Export failed: invalid response");
          return;
        }

        if (blob.type && blob.type.includes("application/json")) {
          try {
            const text = await blob.text();
            const body = text ? JSON.parse(text) : {};
            toast.error(body.message || body.detail || "Export failed");
          } catch {
            toast.error("Export failed");
          }
          return;
        }

        const cd =
          response.headers["content-disposition"] ||
          response.headers["Content-Disposition"];
        let fileName = "leads.xlsx";

        if (cd) {
          let m = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
          if (m?.[1]) {
            fileName = decodeURIComponent(m[1].replace(/"/g, "").trim());
          } else {
            m = cd.match(/filename="?([^";\n]+)"?/i);
            if (m?.[1]) fileName = m[1].trim();
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Export success");
      },
      onError: (error) => {
        console.error("Export failed", error);
        const msg =
          typeof error === "string"
            ? error
            : error?.message || error?.detail || "Export failed";
        toast.error(msg);
      },
    });
  };

  let buttonText = "Export XLSX";
  if (isMobile) buttonText = "";

  /* ---------------- UI ---------------- */

  return (
    <PageContainer>
      <div className="d-flex align-center justify-between mb-4">
        <h2 className="text-primary mb-3">Lead Management</h2>
        <div>
          <Button onClick={handleExport} className="d-flex align-center gap-1">
            <Download />
            {buttonText}
          </Button>
        </div>
      </div>

      <LeadsFilters
        filterAgent={filterAgent}
        setFilterAgent={setFilterAgent}
        filterProperties={filterProperties}
        setFilterProperties={setFilterProperties}
        filterCategory={filterCategory}
        setFilterCategory={handleCategoryChange}
        filterSource={filterSource}
        setFilterSource={setFilterSource}
        filterIntent={filterIntent}
        setFilterIntent={setFilterIntent}
        sortDate={sortDate}
        setSortDate={setSortDate}
        agents={agentOptions}
        properties={propertyOptions}
      />

      <div className="d-flex align-center justify-between mb-2 flex-wrap gap-2">
        {!isLeadsLoading && leads.length > 0 && (
          <div className="text-muted">
            Showing {leads.length} of {totalLeads} total
            {isLeadsFetching && " · updating…"}
          </div>
        )}

        <div className="d-flex align-center gap-1">
          <label htmlFor="page-size-select" className="text-muted">
            Per page:
          </label>
          <select id="page-size-select" value={pageSize} className="filter-select" onChange={handlePageSizeChange}>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>

      {isLeadsLoading ? (
        <div className="leads-skeleton-grid">
          {Array.from({ length: 8 }).map((_, idx) => (
            <div key={idx.toString() || "skeleton"} className="skeleton" style={{ height: isMobile ? 220 : 120 }} />
          ))}
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center text-muted" style={{ padding: "24px 0" }}>
          No leads found.
        </div>
      ) : (
        <LeadsTable
          isMobile={isMobile}
          leads={leads}
          columns={columns}
          handleResend={handleResend}
        />
      )}

      {!isLeadsLoading && totalPages > 1 && (
        <div className="d-flex align-center my-3 justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </PageContainer>
  );
}
